import { calcDeltaPct } from "./MetricCard";

/**
 * Visual representation of a current-vs-previous delta, shared by HeroMetricCard
 * and MetricsTable so the colour / arrow / sign logic lives in one place.
 *
 * Colour reflects good/bad (not raw direction): for `invertDelta` metrics
 * (churn, CAC, payback) a DECREASE is good (green) and an increase bad (red).
 * Arrows stay directional (↑ for any increase). `pct === null` → "—" with no
 * colour, so colour is never the only signal (WCAG 1.4.1).
 */
export interface DeltaDisplay {
  pct: number | null;
  /** "+3.1%" / "-3.1%" / "—" */
  text: string;
  /** "↑" / "↓" / "" — directional, independent of good/bad. */
  arrow: string;
  /** Tailwind text-colour class. */
  colorClass: string;
}

const formatDeltaPct = (pct: number | null): string => {
  if (pct === null) return "—";
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
};

export const getDeltaDisplay = (
  current: number | null,
  previous: number | null,
  invertDelta = false,
): DeltaDisplay => {
  const pct = current === null ? null : calcDeltaPct(current, previous);
  const up = pct !== null && pct > 0;
  const down = pct !== null && pct < 0;
  const isGood = invertDelta ? down : up;
  const isBad = invertDelta ? up : down;
  return {
    pct,
    text: formatDeltaPct(pct),
    arrow: up ? "↑" : down ? "↓" : "",
    colorClass: isGood
      ? "text-green-600"
      : isBad
        ? "text-red-600"
        : "text-neutral-500",
  };
};
